import { RepairRequest } from '../types/firestore';

export interface RepairRequestOperationalIndexState {
  value: string | null;
  sequence: number | null;
  isAssigned: boolean;
}

export type RepairRequestSubjectSource =
  | 'manualDeviceType'
  | 'equipmentTypeSnapshot'
  | 'subjectSnapshot'
  | 'deviceType'
  | 'inferred';

export interface RepairRequestSubjectIdentity {
  label: string;
  source: RepairRequestSubjectSource;
}

export const REPAIR_REQUEST_OPERATIONAL_INDEX_FALLBACK_LABEL = 'Brak nadanego indeksu operacyjnego';

const normalizeOperationalIndexValue = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeOperationalIndexSequence = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;

  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }

  return parsed;
};

const normalizeTextValue = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeLookup = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const toDisplayCase = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const DEVICE_TYPE_LABEL_MAP: Record<string, string> = {
  pralka: 'Pralka',
  zmywarka: 'Zmywarka',
  laptop: 'Laptop',
  notebook: 'Laptop',
  telewizor: 'Telewizor',
  tv: 'Telewizor',
};

const DEVICE_TYPE_PLACEHOLDERS = new Set([
  '',
  'test',
  'testowe',
  'sprzet',
  'sprzęt',
  'urzadzenie',
  'urządzenie',
  'inne',
  'unknown',
  'brak',
  'n a',
  'na',
]);

const canonicalizeDeviceType = (rawDeviceType: string | undefined): string | null => {
  const trimmed = String(rawDeviceType || '').trim();
  if (!trimmed) {
    return null;
  }

  const normalized = normalizeLookup(trimmed);
  const mapped = DEVICE_TYPE_LABEL_MAP[normalized];
  if (mapped) {
    return mapped;
  }

  if (DEVICE_TYPE_PLACEHOLDERS.has(normalized)) {
    return null;
  }

  return toDisplayCase(trimmed);
};

const inferDeviceTypeFromContext = (request: Partial<RepairRequest>): string | null => {
  const lookupText = normalizeLookup(
    [request.description, request.brand, request.model].filter(Boolean).join(' ')
  );
  if (!lookupText) {
    return null;
  }

  const keywordGroups: Array<{ label: string; keywords: string[] }> = [
    { label: 'Pralka', keywords: ['pralka', 'wiruj', 'bebn', 'pranie'] },
    { label: 'Zmywarka', keywords: ['zmywark', 'naczyn', 'mycie'] },
    { label: 'Laptop', keywords: ['laptop', 'notebook', 'klawiatur', 'matryc'] },
    { label: 'Telewizor', keywords: ['telewizor', 'smart tv', 'tv', 'pilot', 'ekran'] },
  ];

  const match = keywordGroups.find(({ keywords }) =>
    keywords.some((keyword) => lookupText.includes(keyword))
  );

  return match?.label || null;
};

export const getRepairRequestSubjectIdentity = (
  request: Partial<RepairRequest>
): RepairRequestSubjectIdentity => {
  const manualDeviceType = normalizeTextValue(request.manualDeviceType);
  if (manualDeviceType) {
    return {
      label: manualDeviceType,
      source: 'manualDeviceType',
    };
  }

  const equipmentTypeSnapshotName = normalizeTextValue(request.equipmentTypeNameSnapshot);
  if (equipmentTypeSnapshotName) {
    return {
      label: equipmentTypeSnapshotName,
      source: 'equipmentTypeSnapshot',
    };
  }

  if (request.equipmentTypeSelectionType === 'other') {
    return {
      label: 'Inne',
      source: 'equipmentTypeSnapshot',
    };
  }

  const subjectSnapshotLabel = canonicalizeDeviceType(request.subjectSnapshot?.name);
  if (subjectSnapshotLabel) {
    return {
      label: subjectSnapshotLabel,
      source: 'subjectSnapshot',
    };
  }

  const canonicalFromDeviceType = canonicalizeDeviceType(request.deviceType);
  if (canonicalFromDeviceType) {
    return {
      label: canonicalFromDeviceType,
      source: 'deviceType',
    };
  }

  const inferred = inferDeviceTypeFromContext(request);
  if (inferred) {
    return {
      label: inferred,
      source: 'inferred',
    };
  }

  return {
    label: 'Nieokreślony typ sprzętu',
    source: 'deviceType',
  };
};

export const getRepairRequestOperationalIndexState = (
  request: Partial<RepairRequest>
): RepairRequestOperationalIndexState => {
  const value = normalizeOperationalIndexValue(request.serviceOperationalIndex);
  return {
    value,
    sequence: normalizeOperationalIndexSequence(request.serviceOperationalIndexSequence),
    isAssigned: Boolean(value),
  };
};

export const getRepairRequestOperationalIndexLabel = (request: Partial<RepairRequest>): string => {
  return (
    getRepairRequestOperationalIndexState(request).value ||
    REPAIR_REQUEST_OPERATIONAL_INDEX_FALLBACK_LABEL
  );
};

export const getRepairRequestIntakeCodeLabel = (request: Partial<RepairRequest>): string | null => {
  const intakeCode = normalizeOperationalIndexValue(request.requestIndex);
  return intakeCode;
};

export const getRepairRequestDisplayIndex = (request: Partial<RepairRequest>): string => {
  return getRepairRequestOperationalIndexLabel(request);
};

export const getRepairRequestSubjectLabel = (request: Partial<RepairRequest>): string => {
  return getRepairRequestSubjectIdentity(request).label;
};
